import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { YouzuLensStats } from "@/components/youzu-lens/youzu-lens-stats";
import { FeatureChart } from "@/components/feature-chart";
import { YouzuLensSDK, type MatchLevel } from "@/components/youzu-lens/youzu-lens-sdk";
import { MatchLevelSelector } from "@/components/youzu-lens/match-level-selector";
import { Activities } from "@/components/activities";
import { YouzuLensPopular } from "@/components/youzu-lens/youzu-lens-popular";
import { CatalogueSelector } from "@/components/catalogue-selector";
import { useCatalogueSelection } from "@/hooks/use-catalogue-selection";

const YouzuLensPage: React.FC = () => {
  const {
    isLoading: areCataloguesLoading, isError: catalogueError,
    selectedCatalogueId, setSelectedCatalogueId, activeCatalogueId,
  } = useCatalogueSelection();
  const [matchLevel, setMatchLevel] = useState<MatchLevel>("similar");

  return (
    <>
      <main className="flex-1 space-y-6 p-2">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Youzu Lens
          </h2>
          <p className="text-muted-foreground">
            Monitor your visual search performance and analytics
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="playground">Playground</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <YouzuLensStats />

            <Card>
              <CardHeader>
                <CardTitle>Visual Search Trends</CardTitle>
                <CardDescription>Search volume over time</CardDescription>
              </CardHeader>
              <CardContent>
                <FeatureChart
                  featureKey="youzuLens"
                  usersKey="youzuLensUsers"
                  dataLabel="Searches"
                  color="#FF4D8C"
                  showUsers={true}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Popular Search Labels</CardTitle>
                <CardDescription>
                  Most frequently searched items
                </CardDescription>
              </CardHeader>
              <CardContent>
                <YouzuLensPopular />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Visual Search Trends</CardTitle>
                <CardDescription>Search volume over time</CardDescription>
              </CardHeader>
              <CardContent>
                <FeatureChart
                  featureKey="youzuLens"
                  usersKey="youzuLensUsers"
                  dataLabel="Searches"
                  color="#FF4D8C"
                  showUsers={true}
                />
              </CardContent>
            </Card>

            <Activities
              filterEventType="LENS_SEARCH"
              title="Youzu Lens Analytics"
              description="Event history and analytics"
            />
          </TabsContent>

          <TabsContent value="playground" className="space-y-6">
             <div className="w-full mx-auto">
               <Card>
                 <CardHeader className="flex flex-row items-center justify-end gap-4 space-y-0 pb-6">
                  <CatalogueSelector
                    value={selectedCatalogueId}
                    onValueChange={setSelectedCatalogueId}
                  />
                  <MatchLevelSelector
                    value={matchLevel}
                    onValueChange={setMatchLevel}
                  />
                </CardHeader>
                <CardContent>
                  {areCataloguesLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : activeCatalogueId ? (
                    <YouzuLensSDK
                      catalogueId={activeCatalogueId}
                      matchLevel={matchLevel}
                    />
                  ) : (
                    <div className="flex items-center justify-center py-12 text-center">
                      <p className="text-muted-foreground max-w-[400px]">
                        {catalogueError ? "Catalogues are temporarily unavailable." : "Create a catalogue before using the playground."}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </>
  );
};

export default YouzuLensPage;
